import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, SwipeableDrawer } from '@mui/material';
// Deep default imports (rather than the barrel) so consumers' test environments
// that don't transform the @mui/icons-material barrel still resolve each icon.
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Slider, { Settings as SlickSettings } from 'react-slick';
// Base slick layout styles. Required for the thumbnail track to lay out
// horizontally. (The mobile app also imports this globally; duplicate import
// is a no-op.)
import 'slick-carousel/slick/slick.css';
import { Dialog } from '../../Surfaces/Dialog';
import { Box as LayoutBox } from '../../Layout/Box';
import { Typography } from '../../Typography';
import { Button } from '../../Button/button';
import { Tooltip } from '../../Feedback/Tooltip';
import { CircularIconButton } from '../../Buttons/CircularIconButton/CircularIconButton';

/** Where a photo originated. Drives the source icon shown in the header. */
export type PhotoSource = 'inspection' | 'work' | 'details';

export interface GalleryPhoto {
  /** Stable id - used as the selection key */
  id: string;
  /** Photo name/filename */
  name: string;
  /** Image source - URL, base64 data, File object, or null for empty state */
  imageSrc: string | File | null;
  /** Optional date to display */
  date?: string | Date | number;
  /** Optional origin of the photo (inspection / work / details) */
  source?: PhotoSource;
  /**
   * Whether this individual photo may be deleted. Defaults to true.
   * Work-task and inspection photos are typically read-only.
   */
  canDelete?: boolean;
}

/** Optional labels for i18n (defaults to English). */
export interface PhotoGalleryModalLabels {
  /** Close button aria-label and confirm dialog cancel */
  close?: string;
  /** Delete photo button aria-label and confirm dialog title */
  deletePhoto?: string;
  /** Confirm delete dialog message */
  deletePhotoPrompt?: string;
  /** Error message when image fails to load */
  failedToLoadImage?: string;
  /** Cancel button in confirm dialog */
  cancel?: string;
  /** Previous thumbnail arrow aria-label */
  previous?: string;
  /** Next thumbnail arrow aria-label */
  next?: string;
  /** Empty state when no photos are supplied */
  noPhotos?: string;
  /** Tooltip/aria text for an inspection-sourced photo */
  inspectionSource?: string;
  /** Tooltip/aria text for a work-sourced photo */
  workSource?: string;
  /** Tooltip/aria text for a details-sourced photo */
  detailsSource?: string;
}

const DEFAULT_LABELS: Required<PhotoGalleryModalLabels> = {
  close: 'Close',
  deletePhoto: 'Delete photo',
  deletePhotoPrompt: 'Are you sure you want to delete this photo?',
  failedToLoadImage: 'Failed to load image',
  cancel: 'Cancel',
  previous: 'Previous photo',
  next: 'Next photo',
  noPhotos: 'No photos to display',
  inspectionSource: 'From inspection',
  workSource: 'From work',
  detailsSource: 'From details',
};

const SOURCE_ICON: Record<PhotoSource, typeof InfoIcon> = {
  inspection: AssignmentIcon,
  work: CheckCircleIcon,
  details: InfoIcon,
};

export interface PhotoGalleryModalProps {
  /** All photos to display. The carousel shows every photo as a thumbnail. */
  photos: GalleryPhoto[];
  /**
   * Whether the photo list is still being fetched. When true, an empty
   * `photos` array is treated as "loading" rather than "no photos" — the
   * modal shows a spinner instead of the noPhotos empty state.
   * @default false
   */
  isLoadingPhotos?: boolean;
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /**
   * Controlled id of the selected (large) photo. If omitted, selection is
   * managed internally and defaults to the first photo.
   */
  selectedId?: string;
  /** Called when the selected photo changes (thumbnail click / arrow) */
  onSelect?: (id: string) => void;
  /** Optional function to format the date */
  formatDate?: (date: string | Date | number) => string;
  /** Optional callback when delete is confirmed */
  onDelete?: (photo: GalleryPhoto) => Promise<void>;
  /** Optional callback when delete fails (e.g. to show snackbar) */
  onDeleteError?: (error: unknown) => void;
  /** Whether deletion is in progress */
  isDeleting?: boolean;
  /** Optional labels for accessibility and confirm dialog (defaults to English) */
  labels?: PhotoGalleryModalLabels;
  /** Optional aria label id for the modal */
  ariaLabelledBy?: string;
  /** Optional test id for the delete button */
  deleteButtonTestId?: string;
  /** Optional test id for the confirm delete button */
  confirmDeleteButtonTestId?: string;
  /**
   * Presentation style:
   * - `'dialog'` (default): a centered modal dialog.
   * - `'drawer'`: a bottom sheet that slides up, with rounded top corners and
   *   swipe-down-to-close (uses MUI SwipeableDrawer).
   * @default 'dialog'
   */
  variant?: 'dialog' | 'drawer';
  /**
   * Height of the drawer paper when `variant="drawer"`. Accepts any CSS height
   * value. Defaults to `'90dvh'`. Pass a calc() to match a specific content
   * region (e.g. the asset detail content area).
   * @default '90dvh'
   */
  drawerHeight?: string | number;
}

// Light-gray→white gradient used across the whole modal/drawer surface: the
// reverse of the theme's `common.gradientLightGray` token
// (PG_WHITE_TO_LIGHT_GRAY_GRADIENT), so the white edge sits at the bottom and
// blends gently into the meta row + carousel. It's a gradient string, so it
// goes on `background`, not `bgcolor`.
const SURFACE_GRADIENT = 'linear-gradient(to top, #ffffff, #e9e9e9)';

const paperSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '90vw',
  maxWidth: '900px',
  maxHeight: '90vh',
  outline: 'none',
  position: 'relative',
  background: SURFACE_GRADIENT,
  overflow: 'hidden',
  '&.MuiPaper-rounded': {
    borderRadius: '15px',
  },
};

// Top bar holds only the close button (and, in drawer mode, the puller above it).
const topBarSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  px: 1.5,
  pt: 1.5,
  pb: 0.5,
  flexShrink: 0,
};

// Source icon + name + date, shown below the selected image.
const metaRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  px: 3,
  pt: 1.5,
  pb: 1,
  flexShrink: 0,
};

// Rounded top corners match the asset section tabs (16px).
const DRAWER_CORNER_RADIUS = '16px';

const pullerSx = {
  width: 36,
  height: 5,
  borderRadius: 3,
  bgcolor: 'common.lightGray',
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 18px)',
  zIndex: 1,
};

// Transparent so the drawer/dialog surface gradient shows through behind the photo.
const imageStageSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  flex: 1,
  minHeight: '280px',
  background: 'transparent',
  overflow: 'hidden',
};

const imageSx = {
  maxWidth: '100%',
  maxHeight: '55vh',
  objectFit: 'contain',
};

const loadingStateSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 2,
  minHeight: '280px',
  position: 'absolute',
  inset: 0,
};

const carouselWrapSx = {
  position: 'relative',
  // Extra side padding keeps the prev/next arrows off the drawer edge.
  px: 7,
  py: 2,
  flexShrink: 0,
  // Transparent so the surface gradient shows through behind the thumbnails.
  bgcolor: 'transparent',
  // Vertical room so the taller centered thumbnail isn't clipped, and center
  // the slides vertically so the taller one stays aligned with its neighbors.
  '& .slick-list': {
    py: '24px',
  },
  '& .slick-track': {
    display: 'flex',
    alignItems: 'center',
  },
  // Non-centered thumbnails sit dimmed; the centered one is full opacity + taller.
  '& .slick-slide': {
    opacity: 0.5,
    transition: 'opacity 180ms ease',
  },
  '& .slick-center': {
    opacity: 1,
  },
  '& .slick-center .pg-thumb': {
    height: '116px',
  },
};

const thumbBaseSx = {
  width: '64px',
  height: '64px',
  p: 0,
  border: 'none',
  borderRadius: 0,
  overflow: 'hidden',
  cursor: 'pointer',
  bgcolor: 'common.midGray',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'height 180ms ease, width 180ms ease',
};

const arrowButtonSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  bgcolor: 'common.white',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 1,
  '&:hover': { bgcolor: 'common.white' },
  '&.Mui-disabled': { opacity: 0.35 },
};

/** Resolve a photo's imageSrc to a displayable URL, tracking object URLs for File sources. */
// Identifies each photo's resolved source without depending on array/object
// identity, so the effect below only re-runs (and re-creates object URLs)
// when a photo's actual id or imageSrc changes, not on every re-render that
// produces a new `photos` array reference with the same content.
function photosSignature(photos: GalleryPhoto[]): string {
  return photos
    .map((p) => `${p.id}:${typeof p.imageSrc === 'string' ? p.imageSrc : p.imageSrc?.name ?? ''}`)
    .join('|');
}

function usePhotoUrls(photos: GalleryPhoto[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const signature = photosSignature(photos);

  useEffect(() => {
    const created: string[] = [];
    const next: Record<string, string> = {};
    for (const photo of photos) {
      if (typeof photo.imageSrc === 'string') {
        next[photo.id] = photo.imageSrc;
      } else if (photo.imageSrc instanceof File) {
        const url = URL.createObjectURL(photo.imageSrc);
        created.push(url);
        next[photo.id] = url;
      }
    }
    setUrls(next);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
    // Deliberately keyed on `signature`, not `photos` — see photosSignature above.
  }, [signature]);

  return urls;
}

function SourceIcon({
  source,
  labels,
}: {
  source: PhotoSource;
  labels: Required<PhotoGalleryModalLabels>;
}) {
  const Icon = SOURCE_ICON[source];
  const label =
    source === 'inspection'
      ? labels.inspectionSource
      : source === 'work'
      ? labels.workSource
      : labels.detailsSource;
  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        sx={{ display: 'inline-flex', color: 'secondary.main', flexShrink: 0, mt: '2px' }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

/**
 * PhotoGalleryModal displays a collection of photos in a single dialog: a large
 * view of the selected photo with its metadata and source, and a scrollable
 * thumbnail carousel (react-slick) along the bottom to switch between photos.
 *
 * Supports flexible image sources (URL, base64, or File), an optional per-photo
 * delete flow with confirmation, and configurable labels for i18n.
 *
 * @example
 * ```tsx
 * <PhotoGalleryModal
 *   photos={photos}
 *   open={open}
 *   onClose={handleClose}
 *   onDelete={handleDelete}
 * />
 * ```
 */
export function PhotoGalleryModal({
  photos,
  isLoadingPhotos = false,
  open,
  onClose,
  selectedId: controlledSelectedId,
  onSelect,
  formatDate,
  onDelete,
  onDeleteError,
  isDeleting = false,
  labels: labelsProp,
  ariaLabelledBy = 'photo-gallery-modal-title',
  deleteButtonTestId = 'photo_delete_button',
  confirmDeleteButtonTestId = 'photo_confirm_delete_button',
  variant = 'dialog',
  drawerHeight = '90dvh',
}: PhotoGalleryModalProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const urls = usePhotoUrls(photos);

  const [internalSelectedId, setInternalSelectedId] = useState<string | undefined>(
    controlledSelectedId ?? photos[0]?.id
  );
  const selectedId = controlledSelectedId ?? internalSelectedId;
  const selectedIndex = Math.max(
    0,
    photos.findIndex((p) => p.id === selectedId)
  );
  const selectedPhoto = photos[selectedIndex] ?? null;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sliderRef = useRef<Slider | null>(null);
  // Slide the carousel starts on; captured once per open so changing settings
  // later doesn't fight the runtime centering effect below, but re-captured
  // each time the modal opens so reopening with a different selection starts
  // centered on it instead of wherever the carousel was last left.
  const initialSlideRef = useRef(selectedIndex);
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    initialSlideRef.current = selectedIndex;
  }
  wasOpenRef.current = open;

  // Keep internal selection valid when the photo list changes.
  useEffect(() => {
    if (controlledSelectedId !== undefined) {
      return;
    }
    if (!photos.some((p) => p.id === internalSelectedId)) {
      setInternalSelectedId(photos[0]?.id);
    }
  }, [photos, internalSelectedId, controlledSelectedId]);

  // Reset image state whenever the displayed photo changes.
  useEffect(() => {
    setIsLoading(selectedPhoto?.imageSrc != null);
    setHasError(false);
  }, [selectedId, selectedPhoto?.imageSrc]);

  // Reset transient dialog state when closed.
  useEffect(() => {
    if (!open) {
      setIsDeleteDialogOpen(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const selectById = useCallback(
    (id: string) => {
      if (controlledSelectedId === undefined) {
        setInternalSelectedId(id);
      }
      onSelect?.(id);
    },
    [controlledSelectedId, onSelect]
  );

  const goTo = useCallback(
    (index: number) => {
      const target = photos[index];
      if (target) {
        selectById(target.id);
      }
    },
    [photos, selectById]
  );

  // Step the selection by +1/-1 (wrapping) and center the carousel on it.
  // We select directly rather than relying on slick's afterChange, which is
  // unreliable in centerMode — that caused the arrows to only intermittently
  // update the large view.
  const stepBy = useCallback(
    (offset: number) => {
      if (photos.length === 0) {
        return;
      }
      const next = (selectedIndex + offset + photos.length) % photos.length;
      selectById(photos[next].id);
      sliderRef.current?.slickGoTo(next);
    },
    [photos, selectedIndex, selectById]
  );

  // Refs let the slick `afterChange` handler stay referentially stable so the
  // slider settings aren't re-created (and the slider re-initialised) on every
  // parent render.
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const selectByIdRef = useRef(selectById);
  selectByIdRef.current = selectById;

  // When the centered slide changes (swipe / arrows / thumbnail click), select
  // that photo. The slider is the single source of truth for the centered
  // index — we deliberately do NOT drive slickGoTo from a selectedIndex effect,
  // which would fight afterChange and snap selection back.
  const handleCarouselChange = useCallback((index: number) => {
    const target = photosRef.current[index];
    if (target) {
      selectByIdRef.current(target.id);
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleDialogClose = useCallback(
    (_event: object) => {
      onClose();
    },
    [onClose]
  );

  const handleDeleteClick = useCallback(() => setIsDeleteDialogOpen(true), []);

  const handleDeleteCancel = useCallback(() => {
    if (isSubmitting || isDeleting) {
      return;
    }
    setIsDeleteDialogOpen(false);
  }, [isSubmitting, isDeleting]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedPhoto || !onDelete) {
      setIsDeleteDialogOpen(false);
      return;
    }
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onDelete(selectedPhoto);
      setIsDeleteDialogOpen(false);
      // If it was the last photo, close; otherwise the consumer is expected to
      // pass an updated photos[] and selection will fall back to a valid photo.
      if (photos.length <= 1) {
        onClose();
      }
    } catch (error) {
      onDeleteError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedPhoto, onDelete, onClose, onDeleteError, isSubmitting, photos.length]);

  const displayDate = selectedPhoto?.date
    ? formatDate
      ? formatDate(selectedPhoto.date)
      : typeof selectedPhoto.date === 'string'
      ? selectedPhoto.date
      : new Date(selectedPhoto.date).toLocaleString()
    : null;

  const canDeleteCurrent = !!onDelete && !!selectedPhoto && selectedPhoto.canDelete !== false;

  const slickSettings: SlickSettings = useMemo(
    () => ({
      dots: false,
      arrows: false, // we render our own arrows for theming
      // Loop so that any slide — including the first/last — can sit dead center.
      // (With infinite:false, centerMode leaves the edge slides edge-aligned.)
      infinite: true,
      speed: 250,
      // Center the active thumbnail with neighbors peeking on either side.
      // (centerMode is incompatible with variableWidth, so slides are fixed-width.)
      centerMode: true,
      centerPadding: '0px',
      slidesToShow: Math.min(photos.length, 5),
      slidesToScroll: 1,
      swipeToSlide: true,
      initialSlide: initialSlideRef.current,
      afterChange: handleCarouselChange,
      responsive: [
        {
          breakpoint: 600,
          settings: { slidesToShow: Math.min(photos.length, 3) },
        },
      ],
    }),
    [photos.length, handleCarouselChange]
  );

  const currentUrl = selectedPhoto ? urls[selectedPhoto.id] : undefined;
  const showCarousel = photos.length > 1;
  const isDrawer = variant === 'drawer';

  const drawerPaperSx = {
    height: drawerHeight,
    maxHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: SURFACE_GRADIENT,
    borderTopLeftRadius: DRAWER_CORNER_RADIUS,
    borderTopRightRadius: DRAWER_CORNER_RADIUS,
  };

  const metaBlock = selectedPhoto && (
    <LayoutBox sx={metaRowSx}>
      {selectedPhoto.source && <SourceIcon source={selectedPhoto.source} labels={labels} />}
      <LayoutBox sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          id={ariaLabelledBy}
          variant="h2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedPhoto.name}
        </Typography>
        {displayDate && (
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {displayDate}
          </Typography>
        )}
      </LayoutBox>
      {canDeleteCurrent && (
        <CircularIconButton
          onClick={handleDeleteClick}
          color="error"
          disabled={isDeleting || isDeleteDialogOpen}
          sx={{ flexShrink: 0 }}
          aria-label={labels.deletePhoto}
          data-testid={deleteButtonTestId}
        >
          {isDeleting && isDeleteDialogOpen ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DeleteIcon />
          )}
        </CircularIconButton>
      )}
    </LayoutBox>
  );

  const mainContent = (
    <>
      {isDrawer && <LayoutBox sx={pullerSx} aria-hidden />}
      <LayoutBox sx={topBarSx}>
        <CircularIconButton
          size="sm"
          color="secondary"
          aria-label={labels.close}
          onClick={onClose}
          data-testid="photo_gallery_close_button"
        >
          <CloseIcon />
        </CircularIconButton>
      </LayoutBox>

      <LayoutBox sx={imageStageSx}>
        {!selectedPhoto && isLoadingPhotos && (
          <LayoutBox sx={loadingStateSx}>
            <CircularProgress size={40} color="primary" />
          </LayoutBox>
        )}
        {!selectedPhoto && !isLoadingPhotos && (
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {labels.noPhotos}
          </Typography>
        )}
        {isLoading && selectedPhoto?.imageSrc != null && (
          <LayoutBox sx={loadingStateSx}>
            <CircularProgress size={40} color="primary" />
          </LayoutBox>
        )}
        {hasError && (
          <LayoutBox sx={loadingStateSx}>
            <Typography variant="body2" color="error">
              {labels.failedToLoadImage}
            </Typography>
          </LayoutBox>
        )}
        {currentUrl && (
          <Box
            component="img"
            key={selectedPhoto?.id}
            src={currentUrl}
            alt={selectedPhoto?.name}
            sx={{
              ...imageSx,
              display: isLoading || hasError ? 'none' : 'block',
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </LayoutBox>

      {metaBlock}

      {showCarousel && (
        <LayoutBox sx={carouselWrapSx}>
          <CircularIconButton
            size="sm"
            isActive={false}
            color="secondary"
            aria-label={labels.previous}
            onClick={() => stepBy(-1)}
            sx={{ ...arrowButtonSx, left: 10 }}
          >
            <ChevronLeftIcon />
          </CircularIconButton>
          <Slider ref={sliderRef} {...slickSettings}>
            {photos.map((photo, index) => {
              const thumbUrl = urls[photo.id];
              const isSelected = photo.id === selectedId;
              return (
                <Box key={photo.id} sx={{ display: 'flex', justifyContent: 'center', px: 0.75 }}>
                  <Box
                    component="button"
                    type="button"
                    className="pg-thumb"
                    onClick={() => {
                      goTo(index);
                      sliderRef.current?.slickGoTo(index);
                    }}
                    aria-label={photo.name}
                    aria-current={isSelected}
                    data-testid={`photo_thumbnail_${index}`}
                    sx={thumbBaseSx}
                  >
                    {thumbUrl ? (
                      <Box
                        component="img"
                        src={thumbUrl}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : photo.source ? (
                      <SourceIcon source={photo.source} labels={labels} />
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Slider>
          <CircularIconButton
            size="sm"
            isActive={false}
            color="secondary"
            aria-label={labels.next}
            onClick={() => stepBy(1)}
            sx={{ ...arrowButtonSx, right: 10 }}
          >
            <ChevronRightIcon />
          </CircularIconButton>
        </LayoutBox>
      )}
    </>
  );

  const dialogAriaLabelledBy = selectedPhoto ? ariaLabelledBy : undefined;

  return (
    <>
      {isDrawer ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={() => onClose()}
          onOpen={() => undefined}
          disableSwipeToOpen
          disableDiscovery
          aria-labelledby={dialogAriaLabelledBy}
          PaperProps={{ sx: drawerPaperSx }}
        >
          {mainContent}
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleDialogClose}
          showCloseButton={false}
          aria-labelledby={dialogAriaLabelledBy}
          paperSx={paperSx}
          contentSx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          content={mainContent}
        />
      )}

      {onDelete && (
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          title={labels.deletePhoto}
          content={<Typography variant="body2">{labels.deletePhotoPrompt}</Typography>}
          actions={
            <>
              <Button
                color="error"
                shape="pill"
                onClick={handleDeleteConfirm}
                disabled={isDeleting || isSubmitting}
                data-testid={confirmDeleteButtonTestId}
              >
                {labels.deletePhoto}
              </Button>
              <Button
                variant="text"
                color="inherit"
                shape="pill"
                onClick={handleDeleteCancel}
                disabled={isSubmitting || isDeleting}
              >
                {labels.cancel}
              </Button>
            </>
          }
        />
      )}
    </>
  );
}

PhotoGalleryModal.displayName = 'PhotoGalleryModal';
